import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTimer } from "../contexts/TimerContext";
import { useBooking } from "../contexts/BookingContext";
import { api } from "../config/api";
import "./SeatSelection.css";

const prepareDisplayRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const sorted = [...rows].sort(
    (a, b) => (a.gridRowId ?? 999) - (b.gridRowId ?? 999),
  );
  const display = [];
  sorted.forEach((row, i) => {
    if (i > 0) {
      const prev = sorted[i - 1].gridRowId ?? 999;
      const curr = row.gridRowId ?? 999;
      const gap = curr - prev - 1;
      for (let g = 1; g <= gap; g++) {
        display.push({ gridRowId: prev + g, isPassage: true });
      }
    }
    display.push({ ...row, isPassage: false });
  });
  return display;
};

const SeatSelection = ({
  movie,
  showtime,
  theater,
  onSeatSelect,
  onProceed,
  vistaTransactionId: propTransactionId,
  onQuantityClick,
  availableShowtimes = [], //showtimechange
  onShowtimeSwitch,
  isSwitchingShowtime = false,
  pendingSwitchShowtime,
  setPendingSwitchShowtime,
  showtimeSwitcherOpen,
  setShowtimeSwitcherOpen,
}) => {
  const {
    startTimer,
    resetTimer,
    isActive,
    formattedTime,
    isExpired,
    timeRemaining,
  } = useTimer();
  const {
    selectedSeats: bookingSelectedSeats = [],
    setSelectedSeats,
    setShowtime,
    vistaTransactionId: contextTransactionId,
    quantity,
  } = useBooking();

  const [seats, setSeats] = useState([]);

  const seatLayoutRef = useRef(null); // 🔥 AUTO SCALE ADDITION
  const seatGridRef = useRef(null); // 🔥 AUTO SCALE ADDITION
  //const [scale, setScale] = useState(1); // 🔥 AUTO SCALE ADDITION
  const [isScrollable, setIsScrollable] = useState(false);
  const summaryRef = useRef(null);
  const getDate = (st) =>
    st.businessDate ||
    st.showDate ||
    st.originalDate ||
    st.date ||
    new Date().toLocaleDateString("en-CA");

  const formatDate = (d) => {
    return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
  };

  // const todayDate = new Date().toISOString().split("T")[0];
  const todayDate = formatDate(new Date());

  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  // const tomorrowStr = tomorrowDate.toISOString().split("T")[0];
  const tomorrowStr = formatDate(tomorrowDate);

  // Group showtimes by date
  const groupedByDate = {};
  availableShowtimes.forEach((st) => {
    const d = getDate(st);
    if (!groupedByDate[d]) groupedByDate[d] = [];
    groupedByDate[d].push(st);
  });

  // Sort dates
  const sortedDates = Object.keys(groupedByDate).sort();

  const normalizeDate = (d) => {
    if (!d) return null;
    const date = new Date(d);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date value received:", d);
      return null;
    }
    return date.toISOString().split("T")[0];
  };

  const currentDate = normalizeDate(getDate(showtime));

  const displayDates =
    currentDate && groupedByDate[currentDate]
      ? [currentDate]
      : sortedDates.length > 0
        ? [sortedDates[0]]
        : [];
  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  useEffect(() => {
    setLocalSelectedSeats([]);
    setHoverPreviewIds([]);
    setHoveredSeatId(null);
  }, [quantity]);

  useEffect(() => {
    const handleResize = () => {
      if (!seatLayoutRef.current || !seatGridRef.current) return;

      const layoutWidth = seatLayoutRef.current.clientWidth;
      const gridWidth = seatGridRef.current.scrollWidth;

      setIsScrollable(gridWidth > layoutWidth);
    };

    handleResize(); // run once after render

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [seats]);

  useEffect(() => {
    const checkScrollable = () => {
      if (!seatLayoutRef.current || !seatGridRef.current) return;

      const containerWidth = seatLayoutRef.current.offsetWidth;
      const gridWidth = seatGridRef.current.scrollWidth;

      setIsScrollable(gridWidth > containerWidth + 10); // tolerance
    };

    setTimeout(checkScrollable, 100);
    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, [seats]);

  /* Auto-scroll disabled per client request: forcing the page to the Proceed
     button after a full selection prevents the user from easily deselecting
     or changing a seat without manually scrolling back up. Keeping the code
     commented out for easy rollback.
  useEffect(() => {
    const selectedCount = seats
      .flatMap((row) => row.seats)
      .filter((s) => s.isSelected && !s.isEmpty).length;

    if (quantity && selectedCount === quantity && selectedCount > 0) {
      // Small delay to let React finish the render
      setTimeout(() => {
        summaryRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 150);
    }
  }, [seats, quantity]);
  */

  // Use transaction ID from props (location state) first, then fall back to context
  // Extract transaction ID if it's an object
  // Note: typeof null === 'object' in JavaScript, so we need to check for null explicitly
  // const propTransId =
  //   propTransactionId && typeof propTransactionId === "object"
  //     ? propTransactionId.transactionId
  //     : propTransactionId || null;
  // const contextTransId =
  //   contextTransactionId && typeof contextTransactionId === "object"
  //     ? contextTransactionId.transactionId
  //     : contextTransactionId || null;

  // const vistaTransactionId = propTransId || contextTransId;

  const propTransId =
    propTransactionId && typeof propTransactionId === "object"
      ? propTransactionId.transactionId
      : propTransactionId || null;
  const contextTransId =
    contextTransactionId && typeof contextTransactionId === "object"
      ? contextTransactionId.transactionId
      : contextTransactionId || null;

  // After a showtime switch, context has the NEW transaction ID but prop still has the old one.
  // So if context and prop differ, always prefer context — it is always more recent.
  const vistaTransactionId =
    contextTransId && contextTransId !== propTransId
      ? contextTransId
      : propTransId || contextTransId;

  // Get current session ID for validation
  const currentSessionId =
    showtime?.id || showtime?.sessionId || showtime?.vistaShowId;

  // Validate transaction ID matches current session to prevent inconsistency
  useEffect(() => {
    if (vistaTransactionId && currentSessionId) {
      const propSessionId =
        propTransactionId && typeof propTransactionId === "object"
          ? propTransactionId.sessionId
          : null;
      const contextSessId =
        contextTransactionId && typeof contextTransactionId === "object"
          ? contextTransactionId.sessionId
          : null;
      const transactionSessionId = propSessionId || contextSessId;

      if (transactionSessionId && transactionSessionId !== currentSessionId) {
        console.error("Transaction ID mismatch detected!", {
          transactionId: vistaTransactionId,
          transactionSessionId,
          currentSessionId,
          showtimeId: showtime?.id,
        });
        // Don't throw error here, but log it - the parent component should handle this
      }
    }

    // Debug logging
    if (propTransId || contextTransId) {
      // console.log("Transaction ID available in SeatSelection:", {
      //   propTransactionId: propTransId,
      //   contextTransactionId: contextTransId,
      //   finalTransactionId: vistaTransactionId,
      //   currentSessionId,
      // });
    } else {
      console.warn("Transaction ID not available in SeatSelection:", {
        propTransactionId: propTransId,
        contextTransactionId: contextTransId,
        currentSessionId,
      });
    }
  }, [
    propTransactionId,
    contextTransactionId,
    vistaTransactionId,
    currentSessionId,
    showtime?.id,
  ]);

  const safeSelectedSeats = Array.isArray(bookingSelectedSeats)
    ? bookingSelectedSeats
    : [];
  const [localSelectedSeats, setLocalSelectedSeats] = useState([]);
  const [hoverPreviewIds, setHoverPreviewIds] = useState([]);
  const [hoveredSeatId, setHoveredSeatId] = useState(null);

  const [loadingSeats, setLoadingSeats] = useState(true);
  const [areaPrices, setAreaPrices] = useState({}); // Store area-wise prices
  const fetchingRef = useRef(false);
  const lastFetchKeyRef = useRef("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showTncPopup, setShowTncPopup] = useState(false);
  const [tncContent, setTncContent] = useState("");

  useEffect(() => {
    if (showTncPopup) {
      api
        .getPageBySlug("tandc-popup")
        .then((page) => {
          setTncContent(page?.content || "");
        })
        .catch(() => {
          showError("Unable to load Terms & Conditions Popup");
        });
    }
  }, [showTncPopup]);

  // Transform Vista API seat layout to component format
  const transformVistaSeatLayout = (vistaData) => {
    // Vista API returns SeatLayout (capital S), handle both cases
    const seatLayoutData =
      vistaData?.SeatLayout || vistaData?.seatLayout || vistaData;

    if (
      !seatLayoutData ||
      !seatLayoutData.areas ||
      !Array.isArray(seatLayoutData.areas)
    ) {
      console.error("Invalid Vista seat layout data:", {
        vistaData,
        hasSeatLayout: !!vistaData?.seatLayout,
        hasSeatLayoutCapital: !!vistaData?.SeatLayout,
        areas: seatLayoutData?.areas,
      });
      return { layout: [], areaPrices: {} };
    }

    const seatLayout = [];
    // const { areas } = seatLayoutData;
    const sortedAreas = [...seatLayoutData.areas].sort(
      (a, b) => Number(a.areaNum || 999) - Number(b.areaNum || 999),
    );
    const areaPriceMap = {}; // Store area code to price mapping

    // areas.forEach((area) => {
    sortedAreas.forEach((area) => {
      if (!area.rows || !Array.isArray(area.rows)) return;

      // Extract price from area if available
      // Check for price in area object (area.price, area.amount, area.seatPrice, etc.)
      const areaPrice =
        area.price || area.amount || area.seatPrice || area.basePrice || null;
      const areaCode = area.areaCode || area.areaNum || "unknown";

      // Store area price mapping
      if (areaPrice !== null && areaPrice !== undefined) {
        areaPriceMap[areaCode] = areaPrice;
      }

      area.rows.forEach((row) => {
        if (!row.seats || !Array.isArray(row.seats)) return;

        const rowId = row.rowPhyId || row.rowId || "A";
        const rowSeats = [];
        let seatCounter = 1; // Sequential counter for non-empty seats only

        // Process seats in the order they come from the API to preserve rendering order
        row.seats.forEach((seat, seatIndexInRow) => {
          // Handle empty seats (isEmpty: true) as vacant spaces
          if (seat.isEmpty || seat.gridSeatNum === 0) {
            // Use index to ensure unique keys even if gridSeatNum is the same
            // Format: empty-{rowId}-{index}-{gridSeatNum} to ensure uniqueness
            rowSeats.push({
              // id: `empty-${rowId}-${seatIndexInRow}-${seat.gridSeatNum ?? 0}`,
              id: `empty-${areaCode}-${rowId}-${seatIndexInRow}-${seat.gridSeatNum ?? 0}`,
              number: 0, // Empty seats don't get a number
              originalNumber: seat.gridSeatNum || 0, // Store original for reference
              row: rowId,
              isEmpty: true, // Mark as empty/vacant space
              isBooked: false,
              isSelected: false,
              isAisle: false,
            });
            return; // Don't increment counter for empty seats
          }

          const seatId = seat.seatId || `${rowId}${seat.gridSeatNum}`;
          // const isBooked = seat.status === "booked" || seat.seatStatus === "1";
          // const isAvailable =
          //   seat.status === "available" || seat.seatStatus === "0";
          // AFTER
          const isHouseOrSpecial =
            seat.status === "houseOrSpecial" ||
            seat.seatStatus === "houseOrSpecial" ||
            seat.houseOrSpecial === true ||
            seat.houseOrSpecial === "1" ||
            seat.type === "houseOrSpecial";
          const isBooked =
            seat.status === "booked" ||
            seat.seatStatus === "1" ||
            isHouseOrSpecial;
          const isAvailable =
            seat.status === "available" || seat.seatStatus === "0";

          // Use area-wise pricing from showtime - required, no fallback
          // New structure: price[areaCode] is an object with { price, ticketTypeCode, ticketTypeDescription, areaCode, areaName }
          let price = null;
          let ticketTypeCode = null;
          let ticketTypeDescription = null;

          if (
            showtime?.price &&
            typeof showtime.price === "object" &&
            areaCode &&
            showtime.price[areaCode]
          ) {
            const pricingInfo = showtime.price[areaCode];

            // Handle both old format (number) and new format (object)
            if (
              typeof pricingInfo === "object" &&
              pricingInfo.price !== undefined
            ) {
              // New format: object with full pricing info
              price = pricingInfo.price;
              ticketTypeCode = pricingInfo.ticketTypeCode || null;
              ticketTypeDescription =
                pricingInfo.ticketTypeDescription ||
                pricingInfo.areaName ||
                null;
            } else if (typeof pricingInfo === "number") {
              // Old format: just a number (backward compatibility)
              price = pricingInfo;
            } else {
              throw new Error(
                `Invalid price structure for seat ${seatId} with areaCode ${areaCode}. Expected number or object with price property.`,
              );
            }
          } else {
            // Price is required - throw error if not available
            throw new Error(
              `Price not available for seat ${seatId} with areaCode ${areaCode}. Showtime pricing data is missing or incomplete.`,
            );
          }

          // Ensure price is a valid number
          if (typeof price !== "number" || isNaN(price) || price <= 0) {
            throw new Error(
              `Invalid price value for seat ${seatId} with areaCode ${areaCode}. Price: ${price}`,
            );
          }

          // Determine category based on area code or price
          let category = "regular";
          if (area.areaCode) {
            // Map area codes to categories
            if (
              area.areaCode.includes("0000000003") ||
              area.areaCode.includes("0000000005")
            ) {
              category = "vip";
            } else if (area.areaCode.includes("0000000004")) {
              category = "premium";
            } else if (area.areaCode.includes("0000000006")) {
              category = "regular";
            }
          } else {
            // Fallback: categorize by price range
            if (price >= 350) {
              category = "vip";
            } else if (price >= 250) {
              category = "premium";
            } else {
              category = "regular";
            }
          }

          // // Assign sequential number (ignoring empty spaces)
          // const sequentialNumber = seatCounter;
          // seatCounter++; // Increment for next non-empty seat

          rowSeats.push({
            id: seatId, // This is the Vista seatId (format: "0000000006|1|1|2")
            number: seat.seatNum, // Sequential number ignoring empty spaces
            originalNumber: seat.gridSeatNum, // Store original gridSeatNum for reference
            row: rowId,
            displayNumber: `${rowId}${seat.seatNum}`, // Readable format like "A1", "B2" for display
            category: category,
            price: price,
            areaCode: areaCode, // Store area code for reference
            ticketTypeCode: ticketTypeCode, // Store ticket type code for booking
            ticketTypeDescription: ticketTypeDescription, // Store ticket type description for booking
            isBooked: isBooked,
            isHouseOrSpecial: isHouseOrSpecial,
            isAisle: false, // Vista API doesn't provide aisle info, set to false
            isSelected: safeSelectedSeats.some(
              (selected) => selected.id === seatId,
            ),
            vistaSeatId: seat.seatId || seatId, // Keep original Vista seat ID for reference (from API response)
            seatId: seat.seatId || seatId, // Also store as seatId for easy access
            isEmpty: false, // Explicitly mark as not empty
          });
        });

        const visualSeats = [...rowSeats].reverse();

        // console.log("visualSeats", visualSeats);
        // console.log("seatLayout", seatLayout)

        if (visualSeats.length > 0) {
          // Check if row already exists, if so merge seats
          // const existingRow = seatLayout.find((r) => r.row === rowId);
          const existingRow = seatLayout.find(
            (r) => r.row === rowId && r.areaCode === areaCode,
          ); // Adding area code too check for existing row as we have multiple area codes for same named row in each areas

          if (existingRow) {
            existingRow.gridRowId = row.gridRowId ?? existingRow.gridRowId;
            // Merge seats and maintain original order (no sorting)
            const allSeats = [...existingRow.seats, ...visualSeats];

            // Reassign sequential numbers for merged row (only for non-empty seats)
            // let mergedCounter = 1;
            // allSeats.forEach((seat) => {
            //   if (!seat.isEmpty) {
            //     seat.number = mergedCounter;
            //     seat.displayNumber = `${rowId}${mergedCounter}`;
            //     mergedCounter++;
            //   }
            // });

            // Remove duplicates by seat id (important for merged areas)
            const uniqueSeats = [];
            const seen = new Set();

            allSeats.forEach((seat) => {
              if (!seen.has(seat.id)) {
                seen.add(seat.id);
                uniqueSeats.push(seat);
              }
            });

            existingRow.seats = uniqueSeats;
          } else {
            // seatLayout.push({ row: rowId, seats: visualSeats });
            // const rowY = Math.max(...row.seats.map(s => s.seatY || 0));
            const rowY =
              row.seats
                .map((s) => s.seatY)
                .filter((v) => v != null)
                .reduce((a, b) => a + b, 0) / row.seats.length;
            seatLayout.push({
              row: rowId,
              seats: visualSeats,
              gridRowId: row.gridRowId ?? 999,
              seatY: rowY, // ✅ CRITICAL FIX
            });
          }
        }
      });
    });

    // Sort rows alphabetically
    // seatLayout.sort((a, b) => a.row.localeCompare(b.row));
    // seatLayout.sort((a, b) => a.gridRowId - b.gridRowId); // Not used commented on April 27, 2026
    seatLayout.sort((a, b) =>
      (a.row || "").localeCompare(b.row || "", undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );

    return { layout: seatLayout, areaPrices: areaPriceMap };
  };

  // Fetch seat layout from API
  useEffect(() => {
    const fetchSeatLayout = async () => {
      let hasLoadedSeats = false; //showtimechange

      if (isSwitchingShowtime) {
        // console.log("Showtime switch in progress, skipping seat layout fetch");
        setLoadingSeats(false);
        fetchingRef.current = false;
        lastFetchKeyRef.current = "";
        return;
      }
      // Don't fetch if quantity is not selected yet (transaction won't be initialized)
      if (!quantity) {
        // console.log(
        //   "Quantity not selected yet, waiting for quantity selection before loading seat layout",
        // );
        setLoadingSeats(false);
        return;
      }

      // CRITICAL: Check if showtime pricing is available before attempting to fetch
      // The pricing data is required for the seat layout transformation
      const priceIsValid =
        showtime?.price &&
        typeof showtime.price === "object" &&
        !Array.isArray(showtime.price) &&
        Object.keys(showtime.price).length > 0;

      if (!priceIsValid) {
        // console.log(
        //   "Showtime pricing not yet available, waiting for pricing data before loading seat layout",
        //   {
        //     hasShowtime: !!showtime,
        //     hasPrice: !!showtime?.price,
        //     priceType: typeof showtime?.price,
        //     isArray: Array.isArray(showtime?.price),
        //     priceKeys: showtime?.price ? Object.keys(showtime.price) : null,
        //     showtimeKeys: showtime ? Object.keys(showtime) : null,
        //   },
        // );
        setLoadingSeats(false);
        fetchingRef.current = false;
        return;
      }

      // Check if we have Vista IDs
      const vistaCinemaId =
        theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.id;

      // CRITICAL: ALWAYS use the current showtime's session ID for the API call
      // This ensures we use the correct session ID after show switching
      // The showtime is the source of truth for which show we're viewing
      // Extract session ID from showtime - check all possible fields in priority order
      // Priority: id > sessionId > vistaShowId (these should all be the same after switching)
      const showtimeSessionId =
        showtime?.id || showtime?.sessionId || showtime?.vistaShowId;

      // Get session ID from transaction context for validation only (not for API call)
      // This is used to verify the transaction matches the current showtime
      const propSessionId =
        propTransactionId && typeof propTransactionId === "object"
          ? propTransactionId.sessionId
          : null;
      const contextSessId =
        contextTransactionId && typeof contextTransactionId === "object"
          ? contextTransactionId.sessionId
          : null;
      const transactionSessionId = propSessionId || contextSessId;

      // CRITICAL: Use showtime's session ID as the PRIMARY source for API call
      // This is the source of truth - if showtime has a session ID, use it
      // Only fall back to transaction session ID if showtime doesn't have one (shouldn't happen)
      const vistaShowId = showtimeSessionId || transactionSessionId;
      const currentSessionId = showtimeSessionId; // For validation

      // console.log("🔍 Session ID determination for API call:", {
      //   showtimeId: showtime?.id,
      //   showtimeSessionId: showtime?.sessionId,
      //   showtimeVistaShowId: showtime?.vistaShowId,
      //   showtimeSessionIdFinal: showtimeSessionId,
      //   transactionSessionId,
      //   vistaShowIdForAPI: vistaShowId,
      //   showtimeKeys: showtime ? Object.keys(showtime) : null,
      //   showtimeFull: showtime, // Log full showtime object to see what we're working with
      // });

      if (!vistaCinemaId || !vistaShowId) {
        // console.log("Missing Vista IDs, cannot fetch seat layout", {
        //   vistaCinemaId,
        //   vistaShowId,
        //   transactionSessionId,
        //   showtimeId: showtime?.id,
        //   showtimeSessionId: showtime?.sessionId,
        //   showtimeVistaShowId: showtime?.vistaShowId,
        // });
        setLoadingSeats(false);
        fetchingRef.current = false;
        return;
      }

      // CRITICAL CHECK 1: If we have a transaction ID, the session ID MUST match the showtime
      // This is the PRIMARY validation to prevent using old transaction IDs
      if (vistaTransactionId && currentSessionId) {
        // If we have a transaction session ID, it MUST match the current showtime session ID
        if (transactionSessionId) {
          if (
            transactionSessionId !== currentSessionId &&
            transactionSessionId !== vistaShowId
          ) {
            console.warn(
              "🚫 BLOCKING FETCH: Transaction session ID does not match showtime session ID",
              {
                transactionSessionId,
                currentSessionId,
                vistaShowId,
                vistaTransactionId,
                showtimeId: showtime?.id,
                showtimeSessionId: showtime?.sessionId,
                propTransactionId,
                contextTransactionId,
              },
            );
            setLoadingSeats(false);
            fetchingRef.current = false;
            // Clear the fetch key so we can retry when the correct transaction ID arrives
            lastFetchKeyRef.current = "";
            return; // BLOCK fetch with mismatched transaction ID
          }
        } else {
          // We have a transaction ID but no session ID in the transaction object
          // This might be an old transaction ID format - be cautious
          // If the showtime session ID doesn't match what we'd expect, wait
          if (vistaShowId !== currentSessionId) {
            console.warn(
              "🚫 BLOCKING FETCH: Transaction ID exists but session ID missing and showtime mismatch",
              {
                vistaTransactionId,
                currentSessionId,
                vistaShowId,
                hasTransactionSessionId: !!transactionSessionId,
                showtimeId: showtime?.id,
              },
            );
            setLoadingSeats(false);
            fetchingRef.current = false;
            lastFetchKeyRef.current = "";
            return;
          }
        }
      }

      // CRITICAL CHECK 2: If showtime is null/undefined, don't fetch
      // This handles the case where showtime is temporarily cleared during show switching
      if (!showtime) {
        // console.log(
        //   "🚫 BLOCKING FETCH: Showtime is not available (likely during show switch)",
        //   {
        //     vistaTransactionId,
        //     currentSessionId,
        //   },
        // );
        setLoadingSeats(false);
        fetchingRef.current = false;
        return;
      }

      // Create a unique key for this fetch request
      // Include transaction ID to ensure different shows get different keys
      const fetchKey = `${vistaCinemaId}-${vistaShowId}-${vistaTransactionId || "no-trans"}`;

      // CRITICAL: Check for duplicates BEFORE doing anything else
      // This prevents multiple simultaneous fetches for the same data
      if (fetchingRef.current && lastFetchKeyRef.current === fetchKey) {
        // console.log("Skipping duplicate seat layout fetch", {
        //   fetchKey,
        //   isFetching: fetchingRef.current,
        //   lastKey: lastFetchKeyRef.current,
        // });
        return;
      }

      // If we're already fetching a different key, wait for it to complete
      if (fetchingRef.current && lastFetchKeyRef.current !== fetchKey) {
        // console.log("Another fetch in progress, waiting...", {
        //   currentFetchKey: lastFetchKeyRef.current,
        //   newFetchKey: fetchKey,
        // });
        // Don't start new fetch if one is already in progress
        return;
      }

      // Reset seats when showtime/transaction changes (for show switching)
      // Only clear if this is a new fetch (different key)
      if (lastFetchKeyRef.current && lastFetchKeyRef.current !== fetchKey) {
        // console.log("Showtime/transaction changed, clearing seats", {
        //   oldKey: lastFetchKeyRef.current,
        //   newKey: fetchKey,
        // });
        setSeats([]);
      }

      // Mark as fetching and set the key
      fetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;
      setLoadingSeats(true);

      // Log session ID being used for debugging
      if (
        transactionSessionId &&
        transactionSessionId !== (showtime?.id || showtime?.sessionId)
      ) {
        // console.log(
        //   "Using session ID from transaction context (may differ from showtime)",
        //   {
        //     transactionSessionId,
        //     showtimeId: showtime?.id,
        //     showtimeSessionId: showtime?.sessionId,
        //     finalSessionId: vistaShowId,
        //   },
        // );
      }

      try {
        if (vistaCinemaId && vistaShowId) {
          // CRITICAL: Check if transaction ID is available
          // For show switching, we MUST wait for the new transaction ID before fetching
          if (!vistaTransactionId) {
            console.warn(
              "Transaction ID not yet available, waiting for booking initialization...",
              {
                propTransactionId,
                contextTransactionId,
                vistaTransactionId,
                vistaCinemaId,
                vistaShowId,
                quantity,
                showtimeId: showtime?.id,
                showtimeSessionId: showtime?.sessionId,
              },
            );

            // Don't throw error - just wait for transaction to be initialized
            // The transaction will be initialized when quantity is selected
            // The useEffect will automatically run again when vistaTransactionId prop updates
            // No need for setTimeout retry - React will handle it via dependency array
            // console.log(
            //   "Transaction ID not available - useEffect will retry when transaction ID is set",
            // );
            setLoadingSeats(false);
            fetchingRef.current = false;
            // Don't clear lastFetchKeyRef here - we want to prevent duplicate fetches
            return;
          }

          // Verify transaction ID is valid before proceeding
          if (
            typeof vistaTransactionId !== "string" ||
            vistaTransactionId.trim().length === 0
          ) {
            console.warn("Transaction ID is invalid, waiting...", {
              vistaTransactionId,
              type: typeof vistaTransactionId,
            });
            setLoadingSeats(false);
            fetchingRef.current = false;
            return;
          }

          // CRITICAL: Re-read showtime values RIGHT BEFORE API call to ensure we have the latest
          // This prevents using stale values from closure
          const currentShowtimeId = showtime?.id;
          const currentShowtimeSessionId = showtime?.sessionId;
          const currentShowtimeVistaShowId = showtime?.vistaShowId;
          const finalSessionId =
            currentShowtimeId ||
            currentShowtimeSessionId ||
            currentShowtimeVistaShowId ||
            vistaShowId;

          console.error(
            "🔴 CRITICAL: About to make API call with session ID:",
            {
              finalSessionId,
              currentShowtimeId,
              currentShowtimeSessionId,
              currentShowtimeVistaShowId,
              oldVistaShowId: vistaShowId,
              showtimeProp: showtime,
              showtimeId: showtime?.id,
              showtimeSessionId: showtime?.sessionId,
              showtimeVistaShowId: showtime?.vistaShowId,
            },
          );

          // Fetch from Vista API with transaction ID
          // console.log("🚀 Fetching seat layout from Vista API", {
          //   vistaCinemaId,
          //   finalSessionId, // Use the re-read value
          //   transId: vistaTransactionId,
          //   showtimeId: currentShowtimeId,
          //   showtimeSessionId: currentShowtimeSessionId,
          //   showtimeVistaShowId: currentShowtimeVistaShowId,
          //   showtimeKeys: showtime ? Object.keys(showtime) : null,
          //   quantity,
          //   // CRITICAL: Log what session ID will be used in the API call
          //   sessionIdForAPI: finalSessionId,
          //   showtimeObject: showtime,
          //   oldVistaShowId: vistaShowId, // Log the old value for comparison
          // });

          // Validate transaction ID format
          if (
            !vistaTransactionId ||
            typeof vistaTransactionId !== "string" ||
            vistaTransactionId.trim().length === 0
          ) {
            throw new Error(
              "Invalid transaction ID: transaction ID is required and cannot be empty",
            );
          }

          // Validate session ID matches showtime
          if (finalSessionId && vistaShowId !== finalSessionId) {
            console.warn("⚠️ Session ID changed! Using updated value:", {
              oldVistaShowId: vistaShowId,
              newFinalSessionId: finalSessionId,
              showtime,
            });
          }

          let vistaData;
          try {
            // CRITICAL: Use finalSessionId which was re-read from showtime prop
            console.error(
              "🔴 MAKING API CALL NOW with sessionId:",
              finalSessionId,
            );
            vistaData = await api.getVistaSeatLayout(
              vistaCinemaId,
              finalSessionId,
              vistaTransactionId,
            );
          } catch (apiError) {
            console.error("Vista seat layout API error:", {
              error: apiError,
              message: apiError.message,
              vistaCinemaId,
              vistaShowId,
              transId: vistaTransactionId,
            });

            // If it's a transaction-related error, it might be a timing issue
            // Retry once after a short delay
            if (
              apiError.message &&
              (apiError.message.includes("transaction") ||
                apiError.message.includes("Transaction") ||
                apiError.message.includes("404") ||
                apiError.message.includes("not found"))
            ) {
              // console.log(
              //   "Retrying seat layout fetch after delay (possible timing issue)",
              // );
              await new Promise((resolve) => setTimeout(resolve, 1000));

              // Re-read showtime values again before retry
              const retryShowtimeId = showtime?.id;
              const retryShowtimeSessionId = showtime?.sessionId;
              const retryShowtimeVistaShowId = showtime?.vistaShowId;
              const retrySessionId =
                retryShowtimeId ||
                retryShowtimeSessionId ||
                retryShowtimeVistaShowId ||
                finalSessionId;

              // Retry the fetch with updated session ID
              vistaData = await api.getVistaSeatLayout(
                vistaCinemaId,
                retrySessionId,
                vistaTransactionId,
              );
            } else {
              throw apiError;
            }
          }

          // Vista API returns SeatLayout (capital S), check for both
          const hasSeatLayout =
            vistaData && (vistaData.SeatLayout || vistaData.seatLayout);

          if (hasSeatLayout) {
            // Validate that showtime pricing is available before transforming
            // Check if price is an object (not array, not null, not undefined)
            const priceIsValid =
              showtime?.price &&
              typeof showtime.price === "object" &&
              !Array.isArray(showtime.price) &&
              Object.keys(showtime.price).length > 0;

            if (!priceIsValid) {
              console.error("Invalid showtime price structure:", {
                price: showtime?.price,
                priceType: typeof showtime?.price,
                isArray: Array.isArray(showtime?.price),
                keys: showtime?.price ? Object.keys(showtime.price) : null,
                showtimeKeys: showtime ? Object.keys(showtime) : null,
              });
              throw new Error(
                "Showtime area-wise pricing is required but not available. Cannot load seat layout without pricing data.",
              );
            }

            const transformed = transformVistaSeatLayout(vistaData);
            if (transformed.layout && transformed.layout.length > 0) {
              setSeats(transformed.layout);

              // Use area prices from showtime (required, no fallback)
              // showtime.price is now an object where each key (areaCode) contains an object with {price, ticketTypeCode, etc.}
              // We need to extract just the price numbers for areaPrices state
              const showtimeAreaPrices = showtime.price;
              if (Object.keys(showtimeAreaPrices).length === 0) {
                throw new Error(
                  "Showtime area-wise pricing object is empty. Cannot determine seat prices.",
                );
              }

              // Extract just the price numbers from the pricing objects
              const priceNumbers = {};
              Object.keys(showtimeAreaPrices).forEach((areaCode) => {
                const pricingInfo = showtimeAreaPrices[areaCode];
                if (
                  typeof pricingInfo === "object" &&
                  pricingInfo.price !== undefined
                ) {
                  priceNumbers[areaCode] = pricingInfo.price;
                } else if (typeof pricingInfo === "number") {
                  priceNumbers[areaCode] = pricingInfo;
                }
              });

              setAreaPrices(priceNumbers);

              setLoadingSeats(false);
              fetchingRef.current = false;
              setShowErrorPopup(false); //showtimechange
              hasLoadedSeats = true;
              return;
            } else {
              // Vista API returned data but no layout - this shouldn't happen
              console.errors("Vista API returned data but no seat layout", {
                vistaData,
              });
              throw new Error(
                "Seat layout data not found in Vista API response. Please try again.",
              );
            }
          } else {
            // Vista API returned data but layout array is empty — treat same as no layout
            console.warn("Vista API returned data but no seat layout rows", {
              vistaData,
            });
            // Fall through to retry logic below by setting hasSeatLayout to false
          }

          // If we reach here without returning, layout was empty — retry
          const retryDelays = [1500, 2500, 3500];
          let retrySuccess = false;

          for (let attempt = 0; attempt < retryDelays.length; attempt++) {
            if (lastFetchKeyRef.current !== fetchKey) {
              // console.log("Stale fetch, aborting retry");
              return;
            }
            // console.log(
            //   `Seat layout retry attempt ${attempt + 1} after ${retryDelays[attempt]}ms...`,
            // );
            await new Promise((resolve) =>
              setTimeout(resolve, retryDelays[attempt]),
            );

            // Abort if a showtime switch started during our wait
            if (isSwitchingShowtime) {
              // console.log(
              //   "Showtime switch started during retry, aborting fetch",
              // );
              setLoadingSeats(false);
              fetchingRef.current = false;
              return;
            }

            try {
              const retrySessionId =
                showtime?.id ||
                showtime?.sessionId ||
                showtime?.vistaShowId ||
                finalSessionId;

              const retryData = await api.getVistaSeatLayout(
                vistaCinemaId,
                retrySessionId,
                vistaTransactionId,
              );

              const retryHasLayout =
                retryData && (retryData.SeatLayout || retryData.seatLayout);
              if (!retryHasLayout) {
                console.warn(`Retry attempt ${attempt + 1}: still no layout`);
                if (isSwitchingShowtime) {
                  // console.log(
                  //   "Switch in progress during retry, aborting silently",
                  // );
                  setLoadingSeats(false);
                  fetchingRef.current = false;
                  return;
                }
                continue;
              }

              // Validate pricing before transforming
              const priceOk =
                showtime?.price &&
                typeof showtime.price === "object" &&
                !Array.isArray(showtime.price) &&
                Object.keys(showtime.price).length > 0;

              if (!priceOk) {
                throw new Error(
                  "Showtime pricing not available. Cannot load seat layout.",
                );
              }

              const retryTransformed = transformVistaSeatLayout(retryData);
              if (
                !retryTransformed.layout ||
                retryTransformed.layout.length === 0
              ) {
                console.warn(
                  `Retry attempt ${attempt + 1}: layout transformed but empty`,
                );
                continue;
              }

              // Success — apply the layout
              setSeats(retryTransformed.layout);
              const retryPrices = {};
              Object.keys(showtime.price).forEach((areaCode) => {
                const info = showtime.price[areaCode];
                if (typeof info === "object" && info.price !== undefined) {
                  retryPrices[areaCode] = info.price;
                } else if (typeof info === "number") {
                  retryPrices[areaCode] = info;
                }
              });
              setAreaPrices(retryPrices);
              setLoadingSeats(false);
              fetchingRef.current = false;
              retrySuccess = true;
              setShowErrorPopup(false);
              return;
            } catch (retryErr) {
              console.warn(
                `Retry attempt ${attempt + 1} error:`,
                retryErr.message,
              );
            }
          }

          // All retries exhausted
          // All retries exhausted
          if (!retrySuccess && !hasLoadedSeats) {
            if (seats.length > 0) return;

            if (isSwitchingShowtime) {
              // console.log(
              //   "Retries exhausted but showtime switch in progress, suppressing error",
              // );
              setLoadingSeats(false);
              fetchingRef.current = false;
              return;
            }
            showError(
              "Seat layout not available. Please go back and try again.",
            );
            setLoadingSeats(false);
            fetchingRef.current = false;
          }
        }
      } catch (error) {
        console.error("Error fetching seat layout:", error);
        showError(`Error loading seat layout: ${error.message}`);
        setLoadingSeats(false);
        fetchingRef.current = false;
      }
    };

    // Only fetch if we have the necessary data AND quantity is selected
    // Quantity must be selected first to initialize the transaction
    if (theater && showtime && quantity) {
      fetchSeatLayout();
    } else {
      // Reset loading state if we don't have all required data
      setLoadingSeats(false);
    }

    // Cleanup function to reset fetching flag on unmount
    return () => {
      fetchingRef.current = false;
    };
  }, [
    theater?.id,
    theater?.vistaCinemaId,
    theater?.vistaCinemaID,
    showtime?.id,
    showtime?.sessionId,
    showtime?.vistaShowId,
    showtime?.screenId,
    showtime?.price, // Add price to dependencies so it refetches when pricing becomes available
    quantity,
    vistaTransactionId,
    isSwitchingShowtime,
  ]);

  const theaterAddress = useMemo(() => {
    if (!theater) {
      return "";
    }

    if (
      typeof theater.displayAddress === "string" &&
      theater.displayAddress.trim().length > 0
    ) {
      return theater.displayAddress.trim();
    }

    const parts = [
      typeof theater.address === "string" ? theater.address.trim() : "",
      typeof theater.city === "string" ? theater.city.trim() : "",
      typeof theater.state === "string" ? theater.state.trim() : "",
      typeof theater.pincode === "string" ? theater.pincode.trim() : "",
    ].filter(Boolean);

    if (parts.length > 0) {
      return parts.join(", ");
    }

    if (
      typeof theater.location === "string" &&
      theater.location.trim().length > 0
    ) {
      return theater.location.trim();
    }

    return "";
  }, [theater]);

  const theaterLandmark = useMemo(() => {
    if (!theater) {
      return "";
    }

    if (
      typeof theater.landmark === "string" &&
      theater.landmark.trim().length > 0
    ) {
      return theater.landmark.trim();
    }

    if (
      typeof theater.location === "string" &&
      theaterAddress &&
      theater.location.trim().length > 0 &&
      theater.location.trim() !== theaterAddress
    ) {
      return theater.location.trim();
    }

    return "";
  }, [theater, theaterAddress]);

  // Sync seats with global selectedSeats when component mounts or selectedSeats change
  useEffect(() => {
    if (seats.length === 0) return;

    const desiredIds = new Set(safeSelectedSeats.map((s) => s.id));
    const needsUpdate = seats.some((row) =>
      row.seats.some(
        (s) => s.isSelected !== (desiredIds.has(s.id) && !s.isEmpty),
      ),
    );

    if (needsUpdate) {
      const newSeats = seats.map((row) => ({
        ...row,
        seats: row.seats.map((seat) => ({
          ...seat,
          isSelected: desiredIds.has(seat.id) && !seat.isEmpty,
        })),
      }));
      setSeats(newSeats);
    }
    setLocalSelectedSeats(safeSelectedSeats);
  }, [safeSelectedSeats]); // Run when selectedSeats change

  // BookMyShow-style auto-fill: given an anchor seat, find a contiguous
  // block of `qty` seats in the same row/area centered around the anchor.
  // Adjacency is determined by `gridSeatNum` gaps of exactly 1, so aisles,
  // empty/wheelchair spaces, booked seats, and area boundaries all
  // correctly break adjacency (we never auto-select across an aisle or
  // into another area). If the run containing the anchor is shorter than
  // `qty`, we return the largest contiguous block that grows outward from
  // the anchor (right first, then left) — never a partial/gapped block.
  const computeAutoFillBlock = (anchorSeat, rowSeats, qty, areaCode) => {
    if (!anchorSeat || !rowSeats || qty < 1) return null;

    const getPos = (s) =>
      s.gridSeatNum ?? s.originalNumber ?? s.number ?? 0;

    // Filter to non-empty, non-booked seats in the requested area, sorted
    // left-to-right by gridSeatNum. Note: `isSelected` seats are KEPT here
    // so that an anchor can re-pick its own seat; caller decides whether
    // this is a REPLACE or APPEND scenario.
    const row = rowSeats
      .filter(
        (s) =>
          !s.isEmpty &&
          !s.isBooked &&
          (areaCode ? s.areaCode === areaCode : true),
      )
      .sort((a, b) => getPos(a) - getPos(b));

    const anchorIdx = row.findIndex((s) => s.id === anchorSeat.id);
    if (anchorIdx === -1) return null;

    // Build adjacency runs: consecutive indices in `row` whose gridSeatNum
    // differs by exactly 1. Anything else (aisles, empty/wheelchair spaces,
    // booked seats already filtered out, area boundaries) breaks the run.
    const runs = [];
    let current = [row[0]];
    for (let i = 1; i < row.length; i++) {
      if (getPos(row[i]) - getPos(row[i - 1]) === 1) {
        current.push(row[i]);
      } else {
        runs.push(current);
        current = [row[i]];
      }
    }
    runs.push(current);

    // Find the run that contains the anchor.
    const anchorRun = runs.find((r) => r.some((s) => s.id === anchorSeat.id));
    if (!anchorRun || anchorRun.length === 0) return null;

    const anchorPosInRun = anchorRun.findIndex(
      (s) => s.id === anchorSeat.id,
    );

    // Full block available: pick the centered qty-seat sub-block containing
    // the anchor. Tiebreak: prefer extending more to the right (BMS bias).
    if (anchorRun.length >= qty) {
      let bestBlock = null;
      let bestScore = Infinity;
      for (let start = 0; start + qty <= anchorRun.length; start++) {
        const block = anchorRun.slice(start, start + qty);
        if (!block.some((s) => s.id === anchorSeat.id)) continue;
        const blockCenter = start + (qty - 1) / 2;
        const distance = Math.abs(blockCenter - anchorPosInRun);
        // Right-bias tiebreak: same distance → prefer larger `start`
        // (more rightward extension).
        const score = distance * 1000 - start;
        if (score < bestScore) {
          bestScore = score;
          bestBlock = block;
        }
      }
      return bestBlock ?? anchorRun.slice(0, qty);
    }

    // Partial block: grow outward from anchor, right first then left, until
    // we have qty seats or both sides are exhausted. Result is always a
    // physically contiguous subset of the anchor's run.
    const block = [anchorRun[anchorPosInRun]];
    let left = anchorPosInRun - 1;
    let right = anchorPosInRun + 1;
    while (block.length < qty && (left >= 0 || right < anchorRun.length)) {
      if (right < anchorRun.length) {
        block.push(anchorRun[right]);
        right++;
        if (block.length >= qty) break;
      }
      if (left >= 0) {
        block.push(anchorRun[left]);
        left--;
        if (block.length >= qty) break;
      }
    }
    return block;
  };

  // Apply area-wise pricing to a list of seats based on showtime's pricing data.
  // Mutates a copy with current ticket type code, description and price.
  const applyAreaPricing = (seatsToPrice) => {
    if (!showtime?.price || typeof showtime.price !== "object") {
      throw new Error(
        "Showtime area-wise pricing is not available. Cannot set seat prices.",
      );
    }
    return seatsToPrice.map((seat) => {
      if (seat.areaCode && showtime.price[seat.areaCode]) {
        const pricingInfo = showtime.price[seat.areaCode];
        let areaPrice = null;
        let ticketTypeCode = seat.ticketTypeCode || null;
        let ticketTypeDescription = seat.ticketTypeDescription || null;

        if (
          typeof pricingInfo === "object" &&
          pricingInfo.price !== undefined
        ) {
          areaPrice = pricingInfo.price;
          ticketTypeCode = pricingInfo.ticketTypeCode || ticketTypeCode;
          ticketTypeDescription =
            pricingInfo.ticketTypeDescription ||
            pricingInfo.areaName ||
            ticketTypeDescription;
        } else if (typeof pricingInfo === "number") {
          areaPrice = pricingInfo;
        } else {
          throw new Error(
            `Invalid price structure for seat ${seat.id} with areaCode ${seat.areaCode}.`,
          );
        }

        if (
          typeof areaPrice !== "number" ||
          isNaN(areaPrice) ||
          areaPrice <= 0
        ) {
          throw new Error(
            `Invalid price for seat ${seat.id} with areaCode ${seat.areaCode}. Price: ${areaPrice}`,
          );
        }

        return {
          ...seat,
          price: areaPrice,
          ticketTypeCode,
          ticketTypeDescription,
          displayNumber:
            seat.displayNumber || `${seat.row}${seat.number}`,
        };
      } else if (seat.areaCode) {
        throw new Error(
          `Price not available for seat ${seat.id} with areaCode ${seat.areaCode}.`,
        );
      } else {
        throw new Error(
          `Area code not available for seat ${seat.id}. Cannot determine price.`,
        );
      }
    });
  };

  const handleSeatClick = (seat) => {
    try {
      // Prevent clicking on booked seats or empty spaces
      if (seat.isBooked || seat.isEmpty) return;

      // Check if quantity is set
      if (!quantity || quantity < 1 || quantity > 10) {
        showError(
          "Please select the number of tickets (Qty) from the booking page first.",
        );
        return;
      }

      // Get currently selected seats
      const currentlySelected = seats.flatMap((row) =>
        row.seats.filter((s) => s.isSelected),
      );

      // TOGGLE: User clicks an already-selected seat → DESELECT just that seat
      if (currentlySelected.some((s) => s.id === seat.id)) {
        const newSeats = seats.map((row) => ({
          ...row,
          seats: row.seats.map((s) =>
            s.id === seat.id ? { ...s, isSelected: false } : s,
          ),
        }));
        setSeats(newSeats);

        const remaining = applyAreaPricing(
          newSeats.flatMap((row) => row.seats.filter((s) => s.isSelected)),
        );

        if (remaining.length === 0 && isActive) {
          resetTimer();
        }

        setLocalSelectedSeats(remaining);
        setSelectedSeats(remaining);

        if (onSeatSelect) {
          onSeatSelect(remaining);
        }
        return;
      }

      // Same-area check (only if there is a current selection)
      if (currentlySelected.length > 0) {
        const firstSelectedSeat = currentlySelected[0];
        if (seat.areaCode !== firstSelectedSeat.areaCode) {
          showError("All seats must be selected from the same area.");
          return;
        }
      }

      const remainingNeeded = Math.max(0, quantity - currentlySelected.length);

      // Compute auto-fill block for the clicked seat's row.
      // Find the row that actually contains the clicked seat
      // (rows can appear multiple times when they exist in multiple areas).
      // Cross-row selection is allowed: the APPEND branch below preserves
      // selections in other rows while filling the remainingNeeded count
      // from the newly clicked row.
      const rowData = seats.find((r) => r.seats.some((s) => s.id === seat.id));
      if (!rowData) {
        showError("Could not find the row for this seat. Please try again.");
        return;
      }

      // SCENARIO C: full qty → REPLACE (new block of `quantity` seats)
      // SCENARIO B: partial  → APPEND (new block of `remainingNeeded` seats)
      const targetQty = remainingNeeded > 0 ? remainingNeeded : quantity;
      const block = computeAutoFillBlock(
        seat,
        rowData.seats,
        targetQty,
        seat.areaCode,
      );
      if (!block || block.length === 0) {
        const msg =
          remainingNeeded > 1
            ? `Row ${seat.row} doesn't have ${remainingNeeded} contiguous seats available. Please try another row.`
            : `Row ${seat.row} doesn't have a contiguous seat available. Please try another row.`;
        showError(msg);
        return;
      }

      const blockIds = new Set(block.map((s) => s.id));
      let newSeats;

      if (remainingNeeded > 0) {
        // APPEND: preserve existing selections in same row + add new block.
        // Seats in other rows are untouched.
        newSeats = seats.map((row) => ({
          ...row,
          seats: row.seats.map((s) => ({
            ...s,
            isSelected:
              s.row === seat.row
                ? s.isSelected || blockIds.has(s.id)
                : s.isSelected,
          })),
        }));
      } else {
        // REPLACE: clear entire selection, set new block only.
        newSeats = seats.map((row) => ({
          ...row,
          seats: row.seats.map((s) => ({
            ...s,
            isSelected: blockIds.has(s.id),
          })),
        }));
      }
      setSeats(newSeats);

      // Get selected seats from the updated seats
      const selected = applyAreaPricing(
        newSeats.flatMap((row) => row.seats.filter((seat) => seat.isSelected)),
      );

      // Start timer when first seat is selected, reset when all seats are unselected
      if (selected.length === 1 && !isActive) {
        startTimer();
      } else if (selected.length === 0 && isActive) {
        // Reset timer when all seats are unselected
        resetTimer();
      }

      // Update both local and global booking state
      setLocalSelectedSeats(selected);
      setSelectedSeats(selected);

      if (onSeatSelect) {
        onSeatSelect(selected);
      }
    } catch (error) {
      console.error("Error selecting seat:", error);
      showError(error.message);
    }
  };

  const handleSeatHover = (seat) => {
    if (
      seat.isBooked ||
      seat.isEmpty ||
      seat.isSelected ||
      !quantity ||
      quantity < 1 ||
      quantity > 10
    ) {
      setHoverPreviewIds([]);
      setHoveredSeatId(null);
      return;
    }

    // Find the row that actually contains the hovered seat
    // (rows can appear multiple times when they exist in multiple areas)
    const rowData = seats.find((r) => r.seats.some((s) => s.id === seat.id));
    if (!rowData) {
      setHoverPreviewIds([]);
      setHoveredSeatId(null);
      return;
    }

    // Determine how many more seats the user needs (multi-row aware)
    const currentlySelectedCount = seats
      .flatMap((r) => r.seats)
      .filter((s) => s.isSelected).length;
    const remainingNeeded = Math.max(
      1,
      quantity - currentlySelectedCount,
    );

    const block = computeAutoFillBlock(
      seat,
      rowData.seats,
      remainingNeeded,
      seat.areaCode,
    );
    if (block && block.length > 0) {
      setHoverPreviewIds(block.map((s) => s.id));
      setHoveredSeatId(seat.id);
    } else {
      setHoverPreviewIds([]);
      setHoveredSeatId(null);
    }
  };

  const handleSeatLeave = () => {
    setHoverPreviewIds([]);
    setHoveredSeatId(null);
  };

  const getSeatClass = (seat) => {
    // Don't apply seat classes to empty spaces
    if (seat.isEmpty) {
      return "";
    }

    let className = "seat";

    // if (seat.isBooked) {
    //   className += " booked";  .. house seats change
    if (seat.isBooked) {
      className += seat.isHouseOrSpecial ? " booked house-seat" : " booked";
    } else if (seat.isSelected) {
      className += " selected";
    }
    // All available seats are green - no category-based color coding

    if (seat.isAisle) {
      className += " aisle";
    }

    return className;
  };

  const getTotalPrice = () => {
    const selectedSeats = seats
      .flatMap((row) => row.seats)
      .filter((seat) => seat.isSelected && !seat.isEmpty);

    return selectedSeats.reduce((total, seat) => {
      if (!seat.price || typeof seat.price !== "number" || seat.price <= 0) {
        throw new Error(
          `Price not available for selected seat ${seat.id}. Cannot calculate total.`,
        );
      }
      return total + seat.price;
    }, 0);
  };

  const getGST = () => {
    return Math.round(getTotalPrice() * 0.18);
  };

  const getSelectedSeatsCount = () => {
    return seats
      .flatMap((row) => row.seats)
      .filter((seat) => seat.isSelected && !seat.isEmpty).length;
  };

  const getSeatCategories = () => {
    const categories = {};
    seats
      .flatMap((row) => row.seats)
      .filter((seat) => !seat.isEmpty) // Skip empty seats
      .forEach((seat) => {
        if (!seat.isBooked) {
          if (!categories[seat.category]) {
            categories[seat.category] = {
              name:
                seat.category.charAt(0).toUpperCase() + seat.category.slice(1),
              price: seat.price,
              count: 0,
            };
          }
          if (seat.isSelected) {
            categories[seat.category].count++;
          }
        }
      });
    return Object.values(categories);
  };

  // Get area-wise prices from the layout
  const getAreaWisePrices = () => {
    const areaPriceMap = {};

    // Create a map of areaCode to areaName from showtime.areaPricing
    const areaNameMap = {};
    if (showtime?.areaPricing && Array.isArray(showtime.areaPricing)) {
      showtime.areaPricing.forEach((area) => {
        if (area.areaCode) {
          // Use ticketTypeDescription or areaName from areaPricing
          areaNameMap[area.areaCode] =
            area.ticketTypeDescription ||
            area.areaName ||
            `Area ${area.areaCode}`;
        }
      });
    }

    // Extract unique area codes and their prices from seats
    seats
      .flatMap((row) => row.seats)
      .filter((seat) => !seat.isEmpty && seat.areaCode && seat.price)
      .forEach((seat) => {
        if (!areaPriceMap[seat.areaCode]) {
          // Use ticketTypeDescription or areaName from seat, fallback to areaNameMap, then to "Area {code}"
          const displayName =
            seat.ticketTypeDescription ||
            seat.areaName ||
            areaNameMap[seat.areaCode] ||
            `Area ${seat.areaCode}`;
          areaPriceMap[seat.areaCode] = {
            areaCode: seat.areaCode,
            areaName: displayName,
            price: seat.price,
            category: seat.category,
          };
        } else {
          // If multiple prices exist for same area, use the most common one
          // For now, just keep the first one found
        }
      });

    // Also use stored areaPrices from Vista API if available
    // areaPrices should contain just price numbers (not objects)
    Object.keys(areaPrices).forEach((areaCode) => {
      const priceValue = areaPrices[areaCode];
      // Handle both number and object formats (for backward compatibility)
      const priceNumber =
        typeof priceValue === "object" &&
        priceValue !== null &&
        priceValue.price !== undefined
          ? priceValue.price
          : typeof priceValue === "number"
            ? priceValue
            : null;

      if (priceNumber !== null) {
        if (!areaPriceMap[areaCode]) {
          areaPriceMap[areaCode] = {
            areaCode: areaCode,
            areaName: areaNameMap[areaCode] || `Area ${areaCode}`, // Use area name from showtime, fallback to "Area {code}"
            price: priceNumber,
            category: "regular", // Default category if not determined
          };
        } else {
          // Update price from Vista API if available and different
          if (priceNumber !== areaPriceMap[areaCode].price) {
            areaPriceMap[areaCode].price = priceNumber;
          }
          // Update area name if available from showtime
          if (areaNameMap[areaCode] && !areaPriceMap[areaCode].areaName) {
            areaPriceMap[areaCode].areaName = areaNameMap[areaCode];
          }
        }
      }
    });

    // Sort by area code for consistent display
    return Object.values(areaPriceMap).sort((a, b) => {
      const codeA = a.areaCode || "";
      const codeB = b.areaCode || "";
      return codeA.localeCompare(codeB);
    });
  };

  const parseAttributes = (attrString) => {
    const result = {};

    attrString
      .split("|")
      .filter(Boolean)
      .forEach((item) => {
        const [key, value] = item.split("=");
        if (key && value) {
          result[key.toLowerCase()] = value;
        }
      });

    return result;
  };

  return (
    <div className="seat-selection-container">
      <div className="container">
        <div className="row">
          {/* ── Showtime Switcher Bar ── */}
          {availableShowtimes.length > 1 && (
            <div className="col-lg-12">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                  padding: "10px 14px",
                  background: "var(--bs-body-bg, #f8f9fa)",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                }}
              >
                {/* 🔥 LABEL */}
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#212529",
                    marginRight: "6px",
                  }}
                >
                  Showtimes:
                </span>

                {/* 🔥 SHOWTIME CHIPS */}
                {displayDates.map((date) =>
                  groupedByDate[date].map((st) => {
                    const stId = st.id || st.sessionId || st.vistaShowId;
                    const isCurrent = stId === currentSessionId;

                    // ── Status logic (mirrors BookingPage) ──
                    const isElapsed =
                      st.isElapsed === true || st.available === false;
                    const isSoldOut = st.availableSeats === 0;

                    let status = "available";
                    if (isElapsed) {
                      status = "elapsed";
                    } else if (isSoldOut) {
                      status = "sold-out";
                    } else if (
                      typeof st.availableSeats === "number" &&
                      typeof st.totalSeats === "number" &&
                      st.totalSeats > 0
                    ) {
                      const soldSeats = st.totalSeats - st.availableSeats;
                      const occupiedPercent = Math.max(
                        0,
                        Math.min(
                          100,
                          Math.round((soldSeats / st.totalSeats) * 100),
                        ),
                      );
                      if (occupiedPercent >= 55) status = "filling-fast";
                    }

                    const isDisabled =
                      isCurrent ||
                      status === "elapsed" ||
                      status === "sold-out" ||
                      isSwitchingShowtime;

                    return (
                      <button
                        key={stId}
                        onClick={() => {
                          if (isDisabled) return;
                          onShowtimeSwitch(st);
                        }}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "16px",
                          fontSize: "12px",
                          border: isCurrent
                            ? "2px solid #0d6efd"
                            : status === "filling-fast"
                              ? "2px solid #e67e22"
                              : status === "sold-out"
                                ? "2px solid #dc3545"
                                : "1px solid #ced4da",
                          background: isCurrent ? "#e7f1ff" : "#fff",
                          color:
                            isDisabled && !isCurrent ? "#adb5bd" : "#212529",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          lineHeight: "1.2",
                          opacity: status === "elapsed" ? 0.5 : 1,
                        }}
                      >
                        {/* TIME */}
                        <span>{st.time || st.startTime}</span>

                        {/* SCREEN NAME */}
                        {st.screenName && (
                          <span style={{ fontSize: "10px", color: "#6c757d" }}>
                            {st.screenName}
                          </span>
                        )}

                        {/* SCREEN ATTRIBUTES */}
                        {st.screenAttributes && (
                          <span style={{ fontSize: "10px", color: "#6c757d" }}>
                            {
                              parseAttributes(st.screenAttributes)
                                ?.screenattributes
                            }
                          </span>
                        )}
                      </button>
                    );
                  }),
                )}
              </div>

              {/* Confirm switch banner */}
              {pendingSwitchShowtime && (
                <div
                  className="d-flex align-items-center justify-content-between flex-wrap my-2 px-3 py-2 rounded"
                  style={{
                    background: "#fff8e1",
                    border: "1px solid #fbc02d",
                    gap: "12px",
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#5d4037",
                      }}
                    >
                      Switch to{" "}
                      {pendingSwitchShowtime.time ||
                        pendingSwitchShowtime.startTime}
                      ?
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#795548",
                        marginLeft: "8px",
                      }}
                    >
                      Your current seat selection will be cleared.
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => setPendingSwitchShowtime(null)}
                      disabled={isSwitchingShowtime}
                      style={{
                        padding: "5px 14px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        border: "1px solid #ced4da",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={() => onShowtimeSwitch(pendingSwitchShowtime)}
                      disabled={isSwitchingShowtime}
                      style={{
                        padding: "5px 16px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        border: "none",
                        background: isSwitchingShowtime ? "#adb5bd" : "#0d6efd",
                        color: "#fff",
                        cursor: isSwitchingShowtime ? "not-allowed" : "pointer",
                        fontWeight: "500",
                      }}
                    >
                      {isSwitchingShowtime ? "Switching..." : "Confirm"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Left Column - Seat Layout */}
          <div className="col-lg-8">
            <div
              className="seat-layout"
              ref={seatLayoutRef}
              style={{
                maxHeight: "70vh",
                overflowY: "auto",
                overflowX: "auto", // ✅ allow horizontal scroll
              }}
            >
              {loadingSeats ? (
                <div className="loading-seats">
                  <div className="loading-spinner"></div>
                  <p>Loading seat layout...</p>
                </div>
              ) : seats.length === 0 ? (
                <div className="no-seats-available">
                  <p>No seats available for this show</p>
                </div>
              ) : (
                <>
                  <div
                    className={`seats-grid ${isScrollable ? "scrollable-layout" : "centered-layout"}`}
                    ref={seatGridRef}
                  >
                    {(() => {
                      // Group rows by area code
                      // const rowsByArea = new Map();

                      // seats.forEach((row) => {
                      //   // Find the area code from the first non-empty seat in this row
                      //   const firstNonEmptySeat = row.seats.find(
                      //     (s) => !s.isEmpty && s.areaCode,
                      //   );
                      //   if (firstNonEmptySeat && firstNonEmptySeat.areaCode) {
                      //     const areaCode = firstNonEmptySeat.areaCode;
                      //     const areaName =
                      //       firstNonEmptySeat.ticketTypeDescription ||
                      //       firstNonEmptySeat.areaName ||
                      //       "Area";
                      //     const areaPrice = firstNonEmptySeat.price || 0;

                      //     if (!rowsByArea.has(areaCode)) {
                      //       rowsByArea.set(areaCode, {
                      //         areaCode: areaCode,
                      //         areaName: areaName,
                      //         price: areaPrice,
                      //         rows: [],
                      //       });
                      //     }

                      //     rowsByArea.get(areaCode).rows.push(row);
                      //   }
                      // });

                      const rowsByArea = new Map();

                      seats.forEach((row) => {
                        const areaCodesInRow = new Set();

                        // Find all unique area codes present in this row
                        row.seats.forEach((seat) => {
                          if (!seat.isEmpty && seat.areaCode) {
                            areaCodesInRow.add(seat.areaCode);
                          }
                        });

                        // Rebuild one version of the row per area
                        areaCodesInRow.forEach((areaCode) => {
                          const firstSeat = row.seats.find(
                            (seat) =>
                              !seat.isEmpty && seat.areaCode === areaCode,
                          );

                          if (!firstSeat) return;

                          if (!rowsByArea.has(areaCode)) {
                            rowsByArea.set(areaCode, {
                              areaCode,
                              areaName:
                                firstSeat.ticketTypeDescription ||
                                firstSeat.areaName ||
                                "Area",
                              price: firstSeat.price || 0,
                              rows: [],
                            });
                          }

                          // Preserve original row structure and seat positions
                          const rebuiltSeats = row.seats
                            .map((seat) => {
                              // Keep already empty seats as true placeholders
                              if (seat.isEmpty || seat.gridSeatNum === 0) {
                                return {
                                  ...seat,
                                  isEmpty: true,
                                  seatNum: 0,
                                };
                              }

                              // Keep seats only for this area
                              if (seat.areaCode === areaCode) {
                                return {
                                  ...seat,
                                  seatNum: Number(seat.seatNum) || 0,
                                };
                              }

                              // Convert other area seats into clean placeholders
                              return {
                                seatId: `empty-${areaCode}-${row.row}-${seat.gridSeatNum}`,
                                gridSeatNum: seat.gridSeatNum,
                                seatNum: 0,
                                isEmpty: true,
                                areaCode,
                              };
                            })
                            .sort(
                              (a, b) =>
                                (a.gridSeatNum || 0) - (b.gridSeatNum || 0),
                            );

                          rowsByArea.get(areaCode).rows.push({
                            ...row,
                            seats: rebuiltSeats,
                          });
                        });
                      });

                      // Convert to array and sort by area code for consistent display
                      const areasArray = Array.from(rowsByArea.values());
                      // const areasArray = Array.from(rowsByArea.values()).sort(
                      //                       (a, b) => (b.price || 0) - (a.price || 0)
                      //                     );

                      // const getAreaTopRowY = (area) => {
                      //   const ys = area.rows
                      //     .flatMap(r => r.seats.map(s => s.seatY))
                      //     .filter(v => v != null);

                      //   if (!ys.length) return null;

                      //   return Math.max(...ys);
                      // };

                      // const areasArray = Array.from(rowsByArea.values()).sort((a, b) => {
                      //   const yA = getAreaTopRowY(a);
                      //   const yB = getAreaTopRowY(b);

                      //   const aIsSingle = (a.rows?.length || 0) === 1;
                      //   const bIsSingle = (b.rows?.length || 0) === 1;

                      //   // 🔥 Special handling ONLY for FRONT single-row areas
                      //   const aIsFrontSingle = aIsSingle && yA != null;
                      //   const bIsFrontSingle = bIsSingle && yB != null;

                      //   // If BOTH are front single → sort by price
                      //   if (aIsFrontSingle && bIsFrontSingle) {
                      //     return (b.price || 0) - (a.price || 0);
                      //   }

                      //   // If ONLY one is front single → it should come first
                      //   if (aIsFrontSingle && !bIsFrontSingle) return -1;
                      //   if (!aIsFrontSingle && bIsFrontSingle) return 1;

                      //   // ✅ Normal case → sort by position
                      //   if (yA != null && yB != null && yA !== yB) {
                      //     return yB - yA;
                      //   }

                      //   // fallback → price
                      //   return (b.price || 0) - (a.price || 0);
                      // });

                      return areasArray.map((areaData, areaIndex) => {
                        // Sort rows within each area alphabetically
                        // const sortedRows = [...areaData.rows].sort((a, b) =>
                        //   a.row.localeCompare(b.row),
                        // );
                        // const sortedRows = [...areaData.rows].sort(
                        //   (a, b) => (a.gridRowId ?? 999) - (b.gridRowId ?? 999)
                        // ); // Not used commented on APril 27, 2026
                        const getRowY = (row) => {
                          if (!row.seats?.length) return null;

                          const ys = row.seats
                            .map((s) => s.seatY)
                            .filter((v) => v != null);
                          if (!ys.length) return null;

                          return ys.reduce((a, b) => a + b, 0) / ys.length;
                        };

                        const sortedRows = [...areaData.rows].sort((a, b) => {
                          const yA = getRowY(a);
                          const yB = getRowY(b);

                          // Use seatY only if valid AND different
                          if (yA != null && yB != null && yA !== yB) {
                            return yB - yA;
                          }

                          // fallback (VERY IMPORTANT for your broken theatre)
                          if (a.gridRowId != null && b.gridRowId != null) {
                            return a.gridRowId - b.gridRowId;
                          }

                          return (a.row || "").localeCompare(
                            b.row || "",
                            undefined,
                            {
                              numeric: true,
                              sensitivity: "base",
                            },
                          );
                        });

                        const maxSeatsInArea = Math.max(
                          ...sortedRows.map((r) => r.seats.length),
                        );

                        const displayRows = prepareDisplayRows(sortedRows);

                        return (
                          <React.Fragment key={areaData.areaCode}>
                            {/* Area Header */}
                            <div className="area-header">
                              <div className="area-name-price">
                                <span className="area-price">
                                  ₹{areaData.price}
                                </span>
                                <span className="area-name">
                                  {areaData.areaName.toUpperCase()}
                                </span>
                              </div>
                              <div className="area-separator"></div>
                            </div>

                            {/* Seats for this area */}
                            {displayRows.map((row, rowIndex) =>
                              row.isPassage ? (
                                <div
                                  key={`passage-${areaData.areaCode}-${row.gridRowId}-${rowIndex}`}
                                  className="seat-passage"
                                />
                              ) : (
                                <div key={row.row} className="seat-row">
                                  <div className="row-label">{row.row}</div>
                                  <div
                                    className="seats-container"
                                    style={{
                                      gridTemplateColumns: `repeat(${maxSeatsInArea}, 24px)`,
                                    }}
                                  >
                                    {[
                                      ...row.seats,
                                      ...Array(
                                        maxSeatsInArea - row.seats.length,
                                      ).fill({ isEmpty: true, filler: true }),
                                    ].map((seat, seatIndex) => {
                                      const isPreview =
                                        !seat.isSelected &&
                                        !seat.isBooked &&
                                        hoverPreviewIds.includes(seat.id);
                                      const isAnchor =
                                        isPreview && hoveredSeatId === seat.id;
                                      return (
                                        <React.Fragment
                                          key={seat.id || `filler-${seatIndex}`}
                                        >
                                          {seat.isEmpty ? (
                                            // Render vacant space for empty seats
                                            <div className="seat-empty-space"></div>
                                          ) : (
                                            <button
                                              className={`${getSeatClass(seat)} ${isPreview ? "preview" : ""} ${isAnchor ? "preview-anchor" : ""}`}
                                              onClick={() => handleSeatClick(seat)}
                                              onMouseEnter={() =>
                                                handleSeatHover(seat)
                                              }
                                              onMouseLeave={handleSeatLeave}
                                              disabled={seat.isBooked}
                                              title={`${seat.displayNumber || seat.id} - ₹${seat.price}`}
                                            >
                                              {seat.number}
                                            </button>
                                          )}
                                          {seat.isAisle && (
                                            <div className="aisle-space"></div>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </div>
                                  <div className="row-label">{row.row}</div>
                                </div>
                              )
                            )}

                            {/* Add spacing after each area (except the last one) */}
                          </React.Fragment>
                        );
                      });
                    })()}

                    {/* SCREEN MOVED INSIDE seats-grid */}
                    <div className="screen-display">
                      <div className="screen-label">Screen This Way</div>
                    </div>
                  </div>
                </>
              )}

              {/* Seat Legend */}
              {!loadingSeats && seats.length > 0 && (
                <div className="seat-legend">
                  {/* Area-based legend using actual area names */}
                  {(() => {
                    // const areaPrices = getAreaWisePrices();
                    const areaPrices = getAreaWisePrices().sort(
                      (a, b) => (b.price || 0) - (a.price || 0),
                    );
                    return areaPrices.map((area) => (
                      <div key={area.areaCode} className="legend-item">
                        <div className="seat"></div>
                        <span>
                          {area.areaName} - ₹{area.price}
                        </span>
                      </div>
                    ));
                  })()}

                  <div className="legend-item">
                    <div className="seat booked"></div>
                    <span>Booked</span>
                  </div>
                  <div className="legend-item">
                    <div className="seat selected"></div>
                    <span>Selected</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Film Info & Booking Summary */}
          {/* Right Column - Booking Summary (full width, centered) */}
          <div className="col-lg-4">
            <div className="sc-summary" ref={summaryRef}>
              <h4>Booking Summary</h4>

              {/* TIMER */}
              {localSelectedSeats.length > 0 && (
                <div className={`sc-timer${isExpired ? " expired" : ""}`}>
                  <span className="sc-timer-icon">⏱</span>
                  <div className="sc-timer-body">
                    <span className="sc-timer-text">
                      {isExpired
                        ? "Session Expired"
                        : `${formattedTime} Mins Remaining`}
                    </span>
                    <div className="sc-timer-bar-bg">
                      <div
                        className="sc-timer-bar"
                        style={{
                          width: isExpired
                            ? "0%"
                            : `${(timeRemaining / (15 * 60)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* MOVIE */}
              <div className="sc-movie">
                <img
                  src={
                    movie?.poster ||
                    movie?.banner ||
                    "/assets/img/film-banner-booking.png"
                  }
                  alt={movie?.title}
                />
                <div className="sc-movie-info">
                  <h5>{movie?.title.replace(/\(.*?\)/g, "").trim()}</h5>

                  <div className="sc-tags">
                    {movie?.genre &&
                      String(movie.genre)
                        .replace(/[{}"]/g, "")
                        .split(",")
                        .map((g) => g.trim())
                        .filter(Boolean)
                        .map((g, i) => (
                          <span key={i} className="sc-tag">
                            {g}
                          </span>
                        ))}
                    {movie?.language && (
                      <span className="sc-tag">{movie.language}</span>
                    )}
                    {(movie?.ageRating || movie?.rating) && (
                      <span className="sc-tag">
                        {movie.ageRating || movie.rating}
                      </span>
                    )}
                  </div>

                  <p>
                    {theater?.name}
                    {showtime?.screenName && ` (${showtime.screenName})`}
                  </p>
                  {theaterAddress && <p>{theaterAddress}</p>}
                  <p>
                    {showtime?.date} • {showtime?.time}
                  </p>
                </div>
              </div>

              {/* SEATS */}
              <div className="sc-seat-section">
                <div className="sc-seat-label">
                  Seat Info
                  {quantity > 0 && (
                    <span className="sc-seat-counter">
                      {" "}
                      ({safeSelectedSeats.length}/{quantity})
                    </span>
                  )}
                </div>

                {seats.flatMap((row) => row.seats.filter((s) => s.isSelected))
                  .length > 0 ? (
                  seats
                    .flatMap((row) => row.seats.filter((s) => s.isSelected))
                    .map((seat, i) => (
                      <div key={seat.id || i} className="sc-seat-row">
                        <span className="sc-seat-cat">
                          {seat.ticketTypeDescription ||
                            seat.areaName ||
                            "REGULAR"}
                        </span>
                        <span className="sc-seat-num">
                          {seat.displayNumber || `${seat.row}${seat.number}`}
                        </span>
                      </div>
                    ))
                ) : (
                  <div className="sc-no-snacks">No seats selected</div>
                )}
              </div>

              {/* PARTIAL / COMPLETE HINTS */}
              {quantity > 0 &&
                safeSelectedSeats.length > 0 &&
                safeSelectedSeats.length < quantity && (
                  <div className="sc-partial-hint">
                    <i className="fas fa-info-circle"></i>
                    {quantity - safeSelectedSeats.length} more seat(s) needed.
                    Click another row to add more.
                  </div>
                )}
              {quantity > 0 &&
                safeSelectedSeats.length === quantity && (
                  <div className="sc-complete-hint">
                    <i className="fas fa-check-circle"></i>
                    All {quantity} seats selected!
                  </div>
                )}

              {/* PRICE */}
              <div className="sc-price-section">
                <div className="sc-price-row">
                  <span>
                    Ticket Price ({safeSelectedSeats.length} × Ticket(s))
                  </span>
                  <span>₹{getTotalPrice().toFixed(2)}</span>
                </div>
              </div>

              {/* TOTAL */}
              <div className="sc-grand-total">
                <span>Grand Total</span>
                <span>₹{getTotalPrice().toFixed(2)}</span>
              </div>

              <div className="sc-actions">
                <button
                  className="sc-cta-btn"
                  onClick={() => {
                    const selected = seats.flatMap((row) =>
                      row.seats.filter((s) => s.isSelected),
                    );

                    if (!quantity || quantity < 1 || quantity > 10) {
                      showError(
                        "Please select the number of tickets (Qty) first.",
                      );
                      return;
                    }

                    if (selected.length !== quantity) {
                      showError(
                        `Please select exactly ${quantity} seat(s). Currently selected: ${selected.length}.`,
                      );
                      return;
                    }

                    if (selected.length > 0) {
                      const firstArea = selected[0].areaCode;
                      if (!selected.every((s) => s.areaCode === firstArea)) {
                        showError(
                          "All seats must be selected from the same area.",
                        );
                        return;
                      }
                    }

                    if (selected.length === 0) {
                      showError("Please select at least one seat");
                      return;
                    }

                    setShowTncPopup(true);
                  }}
                  disabled={
                    !seats
                      .flatMap((row) =>
                        row.seats.filter((s) => s.isSelected),
                      ).length ||
                    (quantity > 0 &&
                      seats.flatMap((row) =>
                        row.seats.filter((s) => s.isSelected),
                      ).length !== quantity)
                  }
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showTncPopup && (
        <div className="tnc-overlay">
          <div className="tnc-modal">
            <h4>Terms & Conditions</h4>

            <div
              className="tnc-content"
              dangerouslySetInnerHTML={{ __html: tncContent }}
            />

            <div className="tnc-actions">
              <button
                className="tnc-reject"
                onClick={() => setShowTncPopup(false)}
              >
                Cancel
              </button>

              <button
                className="tnc-accept"
                onClick={() => {
                  setShowTncPopup(false);
                  if (onProceed) onProceed(); // ✅ session starts ONLY here
                }}
              >
                Accept & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowErrorPopup(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">{errorMessage}</div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatSelection;
